import { domToPng } from "modern-screenshot";

// Origine de l'API Cyrano (VITE_API_BASE en dev, même origine en prod derrière
// le reverse proxy — cf `core/api/client.ts`). Sert à ne forwarder le cookie
// de session (`credentials: "include"`) qu'aux ressources SAME-ORIGIN.
const API_ORIGIN = new URL(
  import.meta.env.VITE_API_BASE || window.location.origin,
  window.location.href,
).origin;

/**
 * `fetchFn` custom pour `domToPng` : ne force `credentials: "include"` que
 * pour les ressources SAME-ORIGIN (API Cyrano, ex. la photo Vault via
 * `buildPreviewUrl`, qui exige le cookie de session WorkOS).
 *
 * ⚠️ **Le bug « profil cassé » dans les PNG vit ici.** La photo « avatar »
 * (`MenuPhotoPicker` → `Creator.avatar_url`, synchronisée Convex→Neon via le
 * webhook `internal_sync.py`) pointe vers un stockage CROSS-ORIGIN (bucket
 * public, ex. `*.digitaloceanspaces.com`). Un fetch credentialed
 * (`credentials: "include"`) vers une ressource `Access-Control-Allow-Origin:
 * *` est REJETÉ par le navigateur (une réponse `*` n'est jamais valide avec
 * des credentials) — et `modern-screenshot` avale cet échec en silence,
 * substituant son placeholder par défaut (un pixel transparent 1×1) à la
 * photo dans le PNG exporté. L'aperçu live n'est jamais affecté : un `<img
 * src>` classique n'envoie PAS les cookies cross-origin par défaut — seul
 * `domToPng`, qui re-fetch chaque image pour l'embarquer en data-URL, le
 * faisait (bug constaté sur les PNG Trista 2026-07-18 : export réussi, mais
 * l'emplacement photo vide).
 *
 * Fix : ne forwarder les credentials qu'aux ressources same-origin (API
 * Cyrano) ; toutes les autres (avatar sur Spaces, ou tout futur host photo
 * externe) sont fetchées sans credentials, comme le fait déjà le navigateur
 * pour un `<img src>` classique.
 */
async function fetchForPngEmbed(url: string): Promise<string | false> {
  let sameOrigin: boolean;
  try {
    sameOrigin = new URL(url, window.location.href).origin === API_ORIGIN;
  } catch {
    sameOrigin = false;
  }
  try {
    const resp = await fetch(url, { credentials: sameOrigin ? "include" : "omit" });
    if (!resp.ok) return false;
    const blob = await resp.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
      reader.readAsDataURL(blob);
    });
  } catch {
    return false;
  }
}

/** Résultat d'un rendu : le PNG data-URL + si LA photo du menu (celle passée
 *  en `photoUrl`, distincte de tout autre asset embarqué — polices, icônes…)
 *  s'est bien embarquée. `photoOk === false` = le PNG existe mais son
 *  emplacement photo est vide (placeholder 1×1 substitué en silence par
 *  `modern-screenshot`) — cf le bug documenté sur `fetchForPngEmbed`. */
export interface RenderMenuPngResult {
  dataUrl: string;
  photoOk: boolean;
}

/** Normalise pour comparer l'URL demandée par `domToPng` à l'URL EXACTE
 *  posée en `src` par les templates (`data.photoUrl`, cf `deriveRender.ts`) —
 *  tolère une différence relative/absolue sans faux négatif. */
function normalizeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return null;
  }
}

/**
 * Rasterise un noeud de menu (petit ou grand format) en PNG data-URL.
 *
 * ⚠️ **Le bug fan « menu tronqué » vivait ici.** `MenuPreview` rend la cible
 * sous un ancêtre `transform: scale(<1)` (le grand format fait 600px de
 * conception, réduit pour tenir dans la colonne d'aperçu ~400px). Or
 * `domToPng(node)` SANS dimensions explicites mesure le noeud via
 * `getBoundingClientRect()`, qui est la boîte **VISUELLE** — donc RÉDUITE par
 * ce scale (~400px). Le contenu cloné, lui, garde sa largeur de layout
 * (600px). Résultat : un canvas trop petit qui coupe la 2e colonne à DROITE
 * **et** le bas du menu (le « TRISTA MENU » que Kayla n'a jamais vu en entier
 * — coupe à droite + en bas).
 *
 * **Fix** : passer `width`/`height` = `offsetWidth`/`offsetHeight` — la boîte de
 * **LAYOUT**, insensible aux transforms des ancêtres. Le canvas fait alors la
 * taille pleine de conception (600×hauteur réelle) → menu complet, aucune coupe.
 * Le petit format (352px < ~400px, scale=1) n'était jamais touché, mais lui
 * passer les mêmes dimensions est correct et sans effet de bord.
 *
 * Un seul point de rendu sert le chemin fan (upload Spaces via
 * `uploadMenuVariantImage`) ET le téléchargement opérateur — un seul fix.
 *
 * `photoUrl` (optionnel) = l'URL EXACTE posée en `src` pour la photo du menu
 * (`identity.photoUrl` côté `MenuTab`). Quand fournie, on observe — SANS
 * dupliquer `fetchForPngEmbed`, seule source de fetch — si CETTE ressource
 * précise a échoué, pour le signaler à l'appelant (`photoOk`) plutôt que de
 * laisser le placeholder muet passer inaperçu.
 */
export async function renderMenuPng(
  node: HTMLElement,
  photoUrl?: string | null,
): Promise<RenderMenuPngResult> {
  const targetPhotoUrl = normalizeUrl(photoUrl);
  let photoFailed = false;
  const fetchFn = async (url: string): Promise<string | false> => {
    const result = await fetchForPngEmbed(url);
    if (result === false && targetPhotoUrl && normalizeUrl(url) === targetPhotoUrl) {
      photoFailed = true;
    }
    return result;
  };
  const dataUrl = await domToPng(node, {
    width: node.offsetWidth,
    height: node.offsetHeight,
    scale: 2,
    backgroundColor: null,
    fetchFn,
  });
  return { dataUrl, photoOk: !photoFailed };
}
